import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'

/**
 * A mecânica de um trilho horizontal: saber quando há para onde rolar, andar de
 * card em card e arrastar com o mouse.
 *
 * Estava privado dentro do AppShowcaseCarousel, que é o banner da home. A vista
 * lista do Explore precisa do mesmo comportamento em várias seções da mesma
 * página, e copiar seria a terceira implementação de rolagem horizontal do
 * diretório (a quarta contando a fila de chips do filtro).
 *
 * Não usa o embla (`components/ui/carousel.tsx`, instalado): o caminho por ele
 * já foi tentado e abandonado no AppShowcaseCarousel — `trimSnaps` travava o
 * último item encostado na borda e exigia stubs de jsdom só para montar no
 * teste. Ver o comentário lá.
 */
interface RailScrollOptions {
  /**
   * Id do elemento que rola. Obrigatório e sem default: a vista lista monta um
   * trilho por categoria na mesma página, e um id fixo viraria id duplicado —
   * que quebra o `aria-controls` das setas.
   */
  id: string
  /**
   * Assinatura do CONTEÚDO do trilho. Mudou, o trilho volta ao primeiro item.
   *
   * Existe por causa do Inertia: os filtros navegam com `preserveState: true`,
   * então trocar de serviço ou de cidade troca as props SEM remontar o
   * componente — e o trilho ficava parado no scroll de antes, mostrando o meio
   * de uma lista nova como se já tivesse sido arrastado. Medido: com o trilho em
   * 700px, filtrar mantinha 700px.
   */
  resetKey?: string
}

export function useRailScroll<T extends HTMLElement>({
  id,
  resetKey,
}: RailScrollOptions) {
  const ref = useRef<T>(null)
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  /**
   * O trilho começa no primeiro item — na montagem e a cada troca de conteúdo.
   *
   * `useLayoutEffect` para corrigir antes da pintura, e um segundo passe no
   * quadro seguinte porque o `scroll-snap-type: mandatory` reposiciona o trilho
   * depois do layout.
   */
  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    element.scrollLeft = 0
    const frame = requestAnimationFrame(() => {
      element.scrollLeft = 0
    })
    return () => cancelAnimationFrame(frame)
  }, [resetKey])

  useEffect(() => {
    const element = ref.current
    if (!element) return

    // A folga de 8px evita que o arredondamento sub-pixel do scroll deixe a seta
    // acesa por um resto de 0,5px que não leva a lugar nenhum.
    const sync = () => {
      setCanScrollPrev(element.scrollLeft > 8)
      setCanScrollNext(
        element.scrollLeft < element.scrollWidth - element.clientWidth - 8,
      )
    }

    sync()
    element.addEventListener('scroll', sync, { passive: true })

    // O trilho é percentual: mudar a largura da janela muda quanto sobra para
    // rolar, e sem isto a seta podia ficar acesa sem ter para onde ir.
    const observer = new ResizeObserver(sync)
    observer.observe(element)

    return () => {
      element.removeEventListener('scroll', sync)
      observer.disconnect()
    }
  }, [])

  // Anda de card em card: o passo é a distância entre o começo de um e o do
  // próximo, e não a largura visível — que, com o card menor que a tela, deixaria
  // um card passar batido.
  const scrollByPage = useCallback((direction: 1 | -1) => {
    const element = ref.current
    if (!element) return

    const [first, second] = element.children
    const stride =
      first && second
        ? (second as HTMLElement).offsetLeft - (first as HTMLElement).offsetLeft
        : element.clientWidth

    element.scrollBy({
      left: direction * (stride > 0 ? stride : element.clientWidth),
      behavior: 'smooth',
    })
  }, [])

  const onPointerDown = useCallback((event: ReactPointerEvent<T>) => {
    if (event.pointerType !== 'mouse') return
    const element = ref.current
    if (!element) return
    drag.current = {
      active: true,
      startX: event.clientX,
      scrollLeft: element.scrollLeft,
      moved: false,
    }
  }, [])

  const onPointerMove = useCallback((event: ReactPointerEvent<T>) => {
    const element = ref.current
    const state = drag.current
    if (!element || !state.active) return

    const delta = event.clientX - state.startX
    if (!state.moved && Math.abs(delta) > 4) {
      state.moved = true
      element.setPointerCapture?.(event.pointerId)
    }
    element.scrollLeft = state.scrollLeft - delta
  }, [])

  const stop = useCallback(() => {
    drag.current.active = false
  }, [])

  // Engole o clique que fecha um arrasto: sem isto, arrastar o trilho por cima
  // de um card abriria a página daquele card ao soltar.
  const onClickCapture = useCallback((event: ReactMouseEvent<T>) => {
    if (!drag.current.moved) return
    event.preventDefault()
    event.stopPropagation()
    drag.current.moved = false
  }, [])

  return {
    ref,
    canScrollPrev,
    canScrollNext,
    scrollByPage,
    dragProps: {
      id,
      onPointerDown,
      onPointerMove,
      onPointerUp: stop,
      onPointerLeave: stop,
      onPointerCancel: stop,
      onClickCapture,
    },
  } as const
}
